<script setup lang="ts">
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import type { RegistrationChannel } from "@/shared/@types/RegistrationChannel";
import TextButton from "@/shared/components/TextButton/TextButton.vue";
import Title from "@/shared/components/Title/Title.vue";
import type { InitialRegistrationData } from "@/app/registration-app/user/shared/@types/InitialRegistrationData";
import type { PersonalRegistrationData } from "@/app/registration-app/user/shared/@types/PersonalRegistrationData";
import type { PreRegistrationValidationData } from "@/app/registration-app/user/shared/@types/PreRegistrationValidationData";
import PreRegistrationValidationForm from "../PreRegistrationValidationForm/PreRegistrationValidationForm.vue";
import PersonalInformationForm from "../PersonalInformationForm/PersonalInformationForm.vue";

defineProps<{
  currentStep: number;
  title: string;
  text: string;
  channel: RegistrationChannel;
  initialData?: InitialRegistrationData;
  isValidatingData?: boolean;
  isRegisteringUser?: boolean;
  validatingDataError?: string;
  showDocumentIdField?: boolean;
}>();

defineEmits<{
  (e: "validate-data", data: PreRegistrationValidationData): void;
  (e: "register-user", data: PersonalRegistrationData): void;
}>();
</script>

<template>
  <div class="registration-wizard">
    <div class="registration-wizard--heading">
      <Title
        size="m"
        :text="title"
      />
      <p class="registration-wizard--text">
        {{
          $t("shared.component.wizard.stepper", {
            index: currentStep,
            length: 2,
          })
        }}
      </p>
    </div>
    <template v-if="currentStep === 1">
      <p class="registration-wizard--text">
        {{ text }}
      </p>
      <PreRegistrationValidationForm
        :is-loading="isValidatingData"
        :error-message="validatingDataError"
        :show-document-id-field="showDocumentIdField"
        :channel="channel"
        :initial-data="initialData"
        @validate-data="$emit('validate-data', $event)"
      />
    </template>
    <PersonalInformationForm
      v-if="currentStep === 2"
      :is-loading="isRegisteringUser"
      :initial-data="initialData"
      @validate-data="$emit('register-user', $event)"
    />
    <TextButton
      :caption="$t('registration.user.backToOverview')"
      icon="arrowBack"
      :to="{ name: AppRouteNames.RegistrationOverview }"
    />
  </div>
</template>

<style scoped>
@import "RegistrationWizard.css";
</style>
